<template>
  <div
    class="c-dropdown"
  >
    <label
      class="u-sr-only"
      :for="ariaSelectId"
    >
      {{ description || placeholder }}
    </label>
    <select
      :name="ariaSelectId"
      class="c-dropdown__select"
      :value="value"
      @change="handleChange"
      @input="handleChange"
    >
      <template v-for="(item, index) in computedOptions">
        <optgroup
          v-if="item.group"
          :key="`${index}-${item.group}`"
          class="c-dropdown__optgroup"
          :label="item.group"
        >
          <option
            v-for="option in item.options"
            :key="option.value"
            :disabled="option.disabled"
            :style="option.style"
            v-bind="option"
            class="c-dropdown__option"
          >
            {{ option.label }}
          </option>
        </optgroup>
        <option
          v-else
          :key="item.value"
          :disabled="item.disabled"
          :style="item.style"
          v-bind="item"
          class="c-dropdown__option"
        >
          {{ item.label }}
        </option>
      </template>
    </select>
  </div>
</template>

<script>
import get from 'lodash/get'
const emptyValues = ['', NaN, null, undefined]
/*
 * welcome to 2006
 * this component bind to @input *and* @change for compatibility with Microsoft Edge
 *
 * option groups can be specified by passing in an array of objects in this format:
 *
 * ```
 * {
 *   group: 'Group Label',
 *   options: [
 *     {key: 'Option label', value: 'Option value'} || 'Option value/label',
 *     // ...
 *   ]
 * }
 * ```
 */
export default {
  name: 'Dropdown',
  props: {
    value: {
      type: [Number, String],
      default: null
    },
    placeholder: {
      type: [String, Boolean],
      default: 'Select an Option'
    },
    description: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
  },
  data () {
    return {
      lastValue: null
    }
  },
  computed: {
    ariaSelectId() {
      return `${this._uid}-dropdown__select`
    },
    placeholderOption () {
      const placeholder = this.placeholder
      const value = this.value
      return {
        label: placeholder || '(Please select one)',
        value: null,
        disabled: true,
        selected: emptyValues.includes(value)
      }
    },
    computedOptions () {
      const options = this.options
      const placeholderOption = this.placeholderOption
      return [
        placeholderOption,
        ...this.normalizeOptions(options)
      ]
    },
    stringValueMap () {
      let result = {}
      for (let item of this.computedOptions) {
        if (item.group) {
          for (let itemOption of item.options) {
            result[`${itemOption.value}`] = itemOption.value
          }
        } else {
          result[`${item.value}`] = item.value
        }
      }
      return result
    }
  },
  methods: {
    normalizeOption (item) {
      const option = get(item, 'label') ? item : { label: item, value: item }
      return {
        ...option,
        selected: `${option.value}` === `${this.value}`
      }
    },
    normalizeOptions (options = []) {
      let result = []
      for (let item of options) {
        if (item.group) {
          result.push({
            ...item,
            options: (item.options || []).map(this.normalizeOption)
          })
        } else {
          result.push(this.normalizeOption(item))
        }
      }
      return result
    },
    handleChange (event) {
      const data = this.stringValueMap[event.target.value]
      const lastValue = this.lastValue
      const value = this.value
      if ((value !== data) && (lastValue !== data)) {
        this.lastValue = data
        this.$emit('input', data)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.c-dropdown {
  width: 100%;
  position: relative;
  &::after {
    position: absolute;
    right: rem(10px);
    bottom: calc(50% - 5px);
    content: "\25BC";
    font-size: 8px;
  }
  &__select {
    width: 100%;
    padding: rem(10px);
    text-align: center;
    text-align-last: center;
    cursor: pointer;
    appearance: none;
    @include themify($themes) {
      border: $border themed('foreground', 'input-border');
      background-color: transparent;
    }
  }
  &__option {
    text-align: left;
  }
}
</style>
